import { Breakpoints, Layout, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import { Calendar, ContactDealer, Info, Pin } from '@volkswagen-onehub/icons-core';
import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { SendConfirmationMessagePageViewProps } from './SendConfirmationMessagePageViewProps';
import { formatDate } from '../../utils/DateUtils';
import { buildDealerAddress } from '../../utils/DealerAddressBuilder';
import { useTracking } from '../../tracking';

// used from https://oneapi.volkswagen.com/lsg-auth/components-core/?path=/docs/examples-icon-table--volkswagen-logo
const iconDefaultSize = '24px';
const detailTabletWidth = '200px';
const detailDesktopWidth = '300px';

const StyledTitle = styled(Text)`
  margin-bottom: ${(props) => props.theme.size.dynamic0150};
`;

const StyledLeadTitleMobile = styled(Text)`
  display: block;
  margin-bottom: ${(props) => props.theme.size.dynamic0150};
  font-size: 24px;

  @media (min-width: ${Breakpoints.b560}px) {
    display: none;
  }
`;

const StyledLeadTitleDesktop = styled(Text)`
  display: none;
  margin-bottom: ${(props) => props.theme.size.dynamic0150};
  font-size: 28px;

  @media (min-width: ${Breakpoints.b560}px) {
    display: block;
  }
`;

const StyledHeaderTitle = styled(Text)`
  font-size: 60px;
  margin-bottom: ${(props) => props.theme.size.dynamic0150};
`;

const LargeBoldText = styled(Text)`
  font-size: 60px; // Set the desired size
  font-weight: bold;
`;

const StyledDetail = styled.div`
  display: flex;
  flex-direction: column;

  .title,
  .details-container {
    margin-left: ${(props) => `calc(${iconDefaultSize} + ${props.theme.size.static200})`};
  }

  .details-container {
    display: flex;
    align-items: center;
    position: relative;

    &__icon {
      position: absolute;
      left: 0;
      width: ${iconDefaultSize};
      height: ${iconDefaultSize};
      display: flex;
      align-items: center;
      justify-content: center;
      transform: ${(props) => `translate(calc(-100% - ${props.theme.size.static200}), 0)`};
    }

    &__text {
      text-wrap: wrap;
      word-break: break-word;
    }
  }
`;

const StyledDetailsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  row-gap: ${(props) => props.theme.size.static350};
  column-gap: ${(props) => props.theme.size.static350};

  @media (min-width: ${Breakpoints.b560}px) {
    width: ${(props) => `calc(${detailTabletWidth} * 2 + ${props.theme.size.static350})`};
    flex-direction: row;

    & > * {
      width: ${detailTabletWidth};
    }

    @media (min-width: ${Breakpoints.b1600}px) {
      width: ${(props) => `calc(${detailDesktopWidth} * 2 + ${props.theme.size.static350})`};
      flex-direction: row;

      & > * {
        width: ${detailDesktopWidth};
      }
    }
`;

const StyledDetailsWrapperInline = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => props.theme.size.static350};
  column-gap: ${(props) => props.theme.size.static200};

  @media (min-width: ${Breakpoints.b560}px) {
    flex-direction: column;
  }

  @media (min-width: ${Breakpoints.b1600}px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;

    & > * {
      max-width: 240px;
      flex-shrink: 0;
    }
  }
`;


function Detail({ icon, text, value }: { icon: ReactElement; text: string; value: string }): ReactElement {
  return (
    <StyledDetail>
      <Text className="title" appearance={TokenTextAppearance.copy100}>
        {text}
      </Text>
      <div className="details-container">
        <div className="details-container__icon">{icon}</div>
        <Text className="details-container__text" appearance={TokenTextAppearance.copy100}>
          {value}
        </Text>
      </div>
    </StyledDetail>
  );
}

export function SendConfirmationMessagePageVwView({
  createdBooking,
  selectedDealer,
  selectedServiceDate,
  noDealer,
}: SendConfirmationMessagePageViewProps): ReactElement {
  const trackingService = useTracking();

  useEffect(() => {
    trackingService.trackFormSubmissionSuccessMessageLoad({
      form: {
        FormName: "Contact",
      }});
  }, []);

  if (noDealer)
    return (
      <Layout allowOverflowingContent>
        <StyledHeaderTitle appearance={TokenTextAppearance.headline400}>
          Service <LargeBoldText>Booking</LargeBoldText>
        </StyledHeaderTitle>
        <StyledLeadTitleMobile appearance={TokenTextAppearance.headline400}>
          Thank you for your <br /> Service Booking request, <br />
          your preferred dealer <br />
          will be in contact soon <br />
          to confirm your booking
        </StyledLeadTitleMobile>
        <StyledLeadTitleDesktop appearance={TokenTextAppearance.headline400}>
          Thank you for your Service Booking request, <br />
          your preferred dealer will be in contact soon <br />
          to confirm your booking
        </StyledLeadTitleDesktop>
        <StyledDetailsWrapperInline>
          <Detail icon={<ContactDealer />} text="Dealer:" value={selectedDealer.name} />
          <Detail icon={<Pin />} text="Address:" value={buildDealerAddress(selectedDealer)} />
          <Detail icon={<Calendar />} text="Date:" value={formatDate(selectedServiceDate, 'short')} />
        </StyledDetailsWrapperInline>
      </Layout>
    );
  else
    return (
      <Layout allowOverflowingContent>
        <StyledTitle appearance={TokenTextAppearance.headline400}>
          Thank you for booking <br /> <Text bold>with Volkswagen</Text>
        </StyledTitle>
        <StyledDetailsWrapper>
          <Detail icon={<Info />} text="Booking No.:" value={createdBooking.bookingId} />
          <Detail icon={<Calendar />} text="Date:" value={formatDate(selectedServiceDate, 'short')} />
          <Detail icon={<ContactDealer />} text="Dealer:" value={selectedDealer.name} />
          <Detail icon={<Pin />} text="Address:" value={buildDealerAddress(selectedDealer)} />
        </StyledDetailsWrapper>
      </Layout>
    );
}
